import { useCallback, useContext, useEffect, useRef } from "react";
import { GestureResponderEvent, Platform, View, useWindowDimensions } from "react-native";
import { DropdownItemsType, DropdownManagerContext } from "../contexts/DropdownManager";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ITEM_HEIGHT } from "../components/Dropdown";
import Constants from "expo-constants";

export type UseDropdownProps = {
    items: DropdownItemsType[];
    keepOpen?: boolean;
    withScreenActionsPadding?: boolean;
    position?: "left" | "right";
    onBlur?: () => void;
};

// Minimum distance between the dropdown and the edges of the screen
const SCREEN_VERTICAL_PADDING = 20;

// Our navigation TabBar's height
const SCREEN_ACTIONS_HEIGHT = 110;

const STATUS_BAR_OFFSET = Platform.OS === "ios" ? Constants.statusBarHeight : 0;

export const useDropdown = ({
    items,
    keepOpen = false,
    position = "left",
    withScreenActionsPadding = true,
    onBlur,
}: UseDropdownProps) => {
    const { setDropdownConfig } = useContext(DropdownManagerContext);
    const { height: windowHeight } = useWindowDimensions();
    const { top: topSafeArea, bottom: bottomSafeArea } = useSafeAreaInsets();
    const ref = useRef<View>(null);

    const onPress = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (e?: GestureResponderEvent, data?: any) => {
            const pressX = e?.nativeEvent?.pageX;
            const pressY = e?.nativeEvent?.pageY;

            ref.current?.measureInWindow((x, y, width) => {
                const windowHeightWithoutScreenActions =
                    windowHeight -
                    STATUS_BAR_OFFSET -
                    topSafeArea -
                    bottomSafeArea -
                    (withScreenActionsPadding ? SCREEN_ACTIONS_HEIGHT : 0) -
                    SCREEN_VERTICAL_PADDING * 2;

                let top = y - STATUS_BAR_OFFSET;
                if (top + items.length * ITEM_HEIGHT > windowHeightWithoutScreenActions) {
                    top = windowHeightWithoutScreenActions - items.length * ITEM_HEIGHT;
                }
                if (top < 0) {
                    top = SCREEN_VERTICAL_PADDING;
                }

                setDropdownConfig({
                    items: items.map((i) => ({
                        ...i,
                        onClick: async () => {
                            await i.onClick(data);
                        },
                    })),
                    position: {
                        x,
                        y: top,
                        width,
                        maxHeight: windowHeightWithoutScreenActions,
                        dropdownPosition: position,
                        pressX,
                        pressY,
                    },
                    keepOpen,
                    onBlur,
                });
            });
        },
        [
            windowHeight,
            topSafeArea,
            bottomSafeArea,
            withScreenActionsPadding,
            items,
            setDropdownConfig,
            position,
            keepOpen,
            onBlur,
        ],
    );

    useEffect(() => {
        setDropdownConfig({ items });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length]);

    return { ref, onPress };
};
